import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../../lib/api";

const initial = {
  name: "",
  email: "",
  phone: "",
  preferred_date: "",
  preferred_time: "",
  coaching_interest: "",
  notes: "",
};

export const DiscoveryCall = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/discovery-call", form);
      toast.success("Discovery call request received. Reza will reach out within 48 hours.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="discovery-call"
      data-testid="discovery-call-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-5">
              Free 30-min discovery call
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0B2B40] tracking-tight leading-tight font-light">
              Let's see if we're a <span className="italic">good fit.</span>
            </h2>
            <p className="mt-6 text-lg text-[#4A5568] font-light leading-relaxed">
              No pitch, no pressure. A quiet conversation about where you are,
              where you want to be, and whether RBICA can help you bridge that
              distance.
            </p>
            <ul className="mt-10 space-y-4 text-[#4A5568]">
              {[
                "Personal & confidential",
                "30 minutes, conducted online",
                "Reza personally reads every request",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1D4E89]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="discovery-call-form"
            onSubmit={submit}
            className="lg:col-span-7 bg-[#F8F9FA] p-8 md:p-12 border border-slate-100"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Full name</label>
                <input
                  data-testid="discovery-name-input"
                  className="input-underline"
                  value={form.name}
                  onChange={handle("name")}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Email</label>
                <input
                  data-testid="discovery-email-input"
                  className="input-underline"
                  type="email"
                  value={form.email}
                  onChange={handle("email")}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Phone (optional)</label>
                <input
                  data-testid="discovery-phone-input"
                  className="input-underline"
                  value={form.phone}
                  onChange={handle("phone")}
                  placeholder="+1 555 0100"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Coaching interest</label>
                <select
                  data-testid="discovery-interest-select"
                  className="input-underline"
                  value={form.coaching_interest}
                  onChange={handle("coaching_interest")}
                >
                  <option value="">Select an area</option>
                  <option>Life Coaching</option>
                  <option>Career &amp; Business</option>
                  <option>Mindset &amp; Spiritual</option>
                  <option>ESL — English Confidence</option>
                  <option>Breathing &amp; Nervous System</option>
                  <option>Not sure yet</option>
                </select>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Preferred date</label>
                <input
                  data-testid="discovery-date-input"
                  className="input-underline"
                  type="date"
                  value={form.preferred_date}
                  onChange={handle("preferred_date")}
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Preferred time</label>
                <input
                  data-testid="discovery-time-input"
                  className="input-underline"
                  type="time"
                  value={form.preferred_time}
                  onChange={handle("preferred_time")}
                />
              </div>
            </div>
            <div className="mt-6">
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5568]">Anything you'd like to share</label>
              <textarea
                data-testid="discovery-notes-input"
                className="input-underline"
                rows={3}
                value={form.notes}
                onChange={handle("notes")}
                placeholder="A few words about what you're navigating..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="discovery-submit-button"
              className="mt-10 bg-[#0B2B40] text-white hover:bg-[#1D4E89] disabled:opacity-60 px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300 w-full sm:w-auto"
            >
              {loading ? "Sending..." : "Request Discovery Call"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default DiscoveryCall;
